import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "./client";
import type { Runbook, RunbookContact } from "./alerts";

export type { Runbook, RunbookContact } from "./alerts";

export interface RunbookInput {
  alert_type: string;
  title: string;
  steps: string[];
  contacts: RunbookContact[];
}

export interface RunbookList {
  items: Runbook[];
  total: number;
}

const runbooksKey = ["runbooks"] as const;

export function useRunbooks() {
  return useQuery({
    queryKey: [...runbooksKey, "list"],
    queryFn: async () => {
      const { data } = await api.get<RunbookList>("/api/v1/runbooks");
      return data;
    },
  });
}

export function useCreateRunbook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RunbookInput) => {
      const { data } = await api.post<Runbook>("/api/v1/runbooks", input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: runbooksKey }),
  });
}

export function useUpdateRunbook(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: Partial<RunbookInput>) => {
      const { data } = await api.put<Runbook>(`/api/v1/runbooks/${id}`, patch);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: runbooksKey }),
  });
}

export function useDeleteRunbook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/runbooks/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: runbooksKey }),
  });
}
